import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft } from "lucide-react";
import { format } from "date-fns";
import SEO, { SITE_URL } from "@/components/SEO";
import Markdown from "@/components/Markdown";
import { API } from "@/lib/apiBase";

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await axios.get(`${API}/blog/${slug}`);
        setPost(response.data);
      } catch (error) {
        console.error("Error fetching blog post:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink">
        <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-ink">
        <h1 className="font-heading text-3xl font-bold text-white mb-4">
          Post Not Found
        </h1>
        <Link to="/blog" className="btn-secondary">
          Back to Blog
        </Link>
      </div>
    );
  }

  const formattedDate = post.created_at
    ? format(new Date(post.created_at), "MMMM d, yyyy")
    : "";

  // Auto-rolling "Last reviewed" date — rolls to the FIRST of the current
  // calendar month every page load. This keeps `dateModified` in the
  // BlogPosting schema fresh without claiming "today" (which Google
  // discounts as auto-stamping). Refreshed monthly is the sweet spot:
  // it signals active maintenance to Google but stays defensible.
  const _now = new Date();
  const lastReviewedDate = new Date(_now.getFullYear(), _now.getMonth(), 1);
  const lastReviewedLabel = format(lastReviewedDate, "MMMM yyyy");
  const lastReviewedISO = lastReviewedDate.toISOString();

  return (
    <div className="page-wrapper bg-ink" data-testid="blog-post-page">
      <SEO
        title={post.title}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
        image={post.image_url}
        type="article"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: post.title,
            description: post.excerpt,
            image: post.image_url,
            url: `${SITE_URL}/blog/${post.slug}`,
            datePublished: post.created_at,
            dateModified: lastReviewedISO,
            author: { "@type": "Organization", name: post.author },
            publisher: { "@id": `${SITE_URL}/#organization` },
            mainEntityOfPage: `${SITE_URL}/blog/${post.slug}`,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
              { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_URL}/blog` },
              { "@type": "ListItem", position: 3, name: post.title, item: `${SITE_URL}/blog/${post.slug}` },
            ],
          },
        ]}
      />
      {/* Header */}
      <section className="py-12 md:py-16 bg-neutral-900">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors duration-200"
            data-testid="back-to-blog"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm uppercase tracking-widest">All Posts</span>
          </Link>

          <div className="flex items-center gap-4 mb-6 text-sm text-gray-400">
            <span>{formattedDate}</span>
            <span>•</span>
            <span>{post.author}</span>
          </div>

          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
            {post.title}
          </h1>
        </div>
      </section>

      {/* Featured Image */}
      {post.image_url && (
        <section className="bg-ink">
          <div className="max-w-5xl mx-auto px-6 md:px-12 -mt-8">
            <div className="aspect-[16/9] overflow-hidden">
              <img
                src={post.image_url}
                alt={post.title}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </section>
      )}

      {/* Content */}
      <section className="py-10 md:py-14 bg-ink">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Markdown className="text-lg leading-relaxed">
            {post.content}
          </Markdown>

          {/* Auto-rolling last-reviewed stamp (refreshes monthly) */}
          <div
            className="mt-12 pt-6 border-t border-neutral-800 text-sm text-gray-500"
            data-testid="last-reviewed"
          >
            Last reviewed:{" "}
            <time dateTime={lastReviewedISO} className="text-gray-300">
              {lastReviewedLabel}
            </time>{" "}
            · Reviewed by Code Planning
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 md:py-14 bg-neutral-900">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-6">
            Have Questions?
          </h2>
          <p className="text-gray-400 max-w-xl mx-auto mb-8">
            Get in touch with our team for expert planning advice.
          </p>
          <Link to="/contact" className="btn-primary inline-block" data-testid="blog-cta">
            Contact Us
          </Link>
        </div>
      </section>
    </div>
  );
}
